import { motion } from "framer-motion";
import { X, Check } from "lucide-react";

interface BeforeAfterSectionProps {
  onCTAClick: () => void;
  className?: string;
}

export default function BeforeAfterSection({ onCTAClick }: BeforeAfterSectionProps) {
  const beforePoints = [
    "بتضيع وقتك في فيديوهات عشوائية",
    "ما عندك خطة تداول",
    "بتخاف من الخسارة",
    "بتحس السوق ضدك",
  ];

  const afterPoints = [
    "عندك خطة واضحة",
    "بتدخل بثقة",
    "بتفهم السوق فعلياً",
    "جزء من مجتمع ناجح بيدعمك",
  ];

  return (
    <section className="relative py-24 bg-gradient-to-br from-[#0A1E3F] to-[#000000] overflow-hidden">
      {/* Background Trading Chart Pattern */}
      <svg className="absolute inset-0 w-full h-full opacity-5" viewBox="0 0 1000 1000">
        <motion.path
          d="M100,800 L200,600 L300,650 L400,400 L500,450 L600,200 L700,300 L800,100 L900,200"
          stroke="#CFAE58"
          strokeWidth="3"
          fill="none"
          initial={{ pathLength: 0, opacity: 0 }}
          whileInView={{ pathLength: 1, opacity: 0.3 }}
          viewport={{ once: true }}
          transition={{ duration: 2 }}
        />
      </svg>

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-6xl mx-auto">
          {/* Grid Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Before */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-red-900/20 to-[#000000]/50 backdrop-blur-sm border border-red-500/30 rounded-2xl p-8 h-full">
                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                  <div className="bg-red-500/20 p-3 rounded-full">
                    <X className="w-8 h-8 text-red-500" />
                  </div>
                  <h3 className="text-3xl text-white">قبل التحدي</h3>
                </div>

                {/* Points */}
                <div className="space-y-4">
                  {beforePoints.map((point, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex items-start gap-3 bg-red-900/10 rounded-lg p-4 border border-red-500/20"
                    >
                      <X className="w-5 h-5 text-red-500 mt-1 flex-shrink-0" />
                      <p className="text-gray-300 text-lg">{point}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* After */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <div className="bg-gradient-to-br from-[#CFAE58]/20 to-[#000000]/50 backdrop-blur-sm border border-[#CFAE58]/50 rounded-2xl p-8 h-full shadow-2xl shadow-[#CFAE58]/20">
                {/* Header */}
                <div className="flex items-center gap-3 mb-8">
                  <div className="bg-[#CFAE58]/20 p-3 rounded-full">
                    <Check className="w-8 h-8 text-[#CFAE58]" />
                  </div>
                  <h3 className="text-3xl text-white">بعد التحدي</h3>
                </div>

                {/* Points */}
                <div className="space-y-4">
                  {afterPoints.map((point, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="flex items-start gap-3 bg-[#CFAE58]/10 rounded-lg p-4 border border-[#CFAE58]/30 hover:border-[#CFAE58]/60 transition-all hover:shadow-lg hover:shadow-[#CFAE58]/20"
                    >
                      <Check className="w-5 h-5 text-[#CFAE58] mt-1 flex-shrink-0" />
                      <p className="text-gray-200 text-lg">{point}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>

          {/* Bottom Quote */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="bg-gradient-to-r from-transparent via-[#CFAE58]/10 to-transparent border-y border-[#CFAE58]/30 py-8 mb-8">
              <p className="text-2xl md:text-3xl text-[#CFAE58] max-w-4xl mx-auto leading-relaxed">
                "في 30 يوم، رح تتغيّر طريقتك في التفكير قبل ما يتغيّر رصيدك."
              </p>
            </div>

<button
  onClick={onCTAClick}
  className="bg-[#CFAE58] text-white px-10 py-4 rounded-xl hover:bg-[#B89A45] transition-all duration-300 
             shadow-[0_0_25px_rgba(207,174,88,0.9),_0_0_50px_rgba(207,174,88,0.6)] 
             transform hover:scale-105 drop-shadow-[0_0_10px_rgba(255,255,255,0.6)]"
>
  سجّل بياناتك وابدأ فورًا
</button>

          </motion.div>
        </div>
      </div>
    </section>
  );
}
